import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Refresh,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import FAQForm from './FAQForm';

interface FAQ {
  _id: string;
  id?: string;
  question: Record<string, string>;
  answer: Record<string, string>;
  category: string;
  page: string;
  tags: string[];
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastUpdatedBy?: {
    name: string;
    email: string;
  };
}

// Helper function to get display text (handles multilingual format)
const getDisplayText = (text: string | Record<string, string> | undefined, maxLength = 100): string => {
  if (!text) return '';
  if (typeof text === 'string') return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  // Multilingual format - prefer EN, fallback to first available
  const value = text.EN || text.en || Object.values(text)[0] || '';
  return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
};

const categoryLabels: Record<string, string> = {
  general: 'General',
  credits: 'Credits',
  premium: 'Premium',
  technical: 'Technical',
  account: 'Account',
};

const pageLabels: Record<string, string> = {
  landing: 'Landing',
  face_swap: 'Face Swap',
  multiple_face_swap: 'Multiple Swap',
  video_face_swap: 'Video Swap',
  photo_enhancer: 'Photo Enhancer',
};

const FAQList: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterPage, setFilterPage] = useState<string>('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [selectedFAQ, setSelectedFAQ] = useState<FAQ | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getAllFAQs();
      if (response.success && response.data) {
        // Sort FAQs by displayOrder
        const sortedFAQs = [...response.data].sort((a, b) => a.displayOrder - b.displayOrder);
        setFaqs(sortedFAQs);
      } else {
        setError(response.error || 'Failed to fetch FAQs');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch FAQs');
      toast.error('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const handleCreate = () => {
    setSelectedFAQ(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (faq: FAQ) => {
    setSelectedFAQ(faq);
    setFormDialogOpen(true);
  };

  const handleDelete = (faq: FAQ) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!faqToDelete) return;

    try {
      const response = await apiService.deleteFAQ(faqToDelete._id);
      if (response.success) {
        toast.success('FAQ deleted successfully');
        fetchFAQs();
        setDeleteDialogOpen(false);
        setFaqToDelete(null);
      } else {
        toast.error(response.error || 'Failed to delete FAQ');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete FAQ');
    }
  };

  const handleToggleStatus = async (faq: FAQ) => {
    try {
      const response = await apiService.toggleFAQStatus(faq._id, !faq.isActive);
      if (response.success) {
        toast.success(`FAQ ${!faq.isActive ? 'activated' : 'deactivated'}`);
        fetchFAQs();
      } else {
        toast.error(response.error || 'Failed to toggle FAQ status');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to toggle FAQ status');
    }
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setSelectedFAQ(null);
    fetchFAQs();
  };

  const handleMoveFAQ = async (faqId: string, direction: 'up' | 'down') => {
    const currentIndex = faqs.findIndex(f => f._id === faqId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= faqs.length) return;

    // Create new array with swapped items
    const newFaqs = [...faqs];
    const tempFaq = newFaqs[currentIndex];
    newFaqs[currentIndex] = newFaqs[newIndex];
    newFaqs[newIndex] = tempFaq;

    // Update displayOrder values
    const orders = newFaqs.map((faq, index) => ({
      id: faq._id,
      displayOrder: index,
    }));

    setFaqs(newFaqs.map((faq, index) => ({ ...faq, displayOrder: index })));

    try {
      await apiService.reorderFAQs(orders);
      toast.success('FAQ order updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update FAQ order');
      fetchFAQs();
    }
  };

  const filteredFAQs = faqs.filter(faq => {
    if (filterCategory && faq.category !== filterCategory) return false;
    if (filterPage && faq.page !== filterPage) return false;
    if (filterActive !== null && faq.isActive !== filterActive) return false;
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const question = getDisplayText(faq.question, 500).toLowerCase();
    const answer = getDisplayText(faq.answer, 500).toLowerCase();
    return question.includes(searchLower) || answer.includes(searchLower);
  });

  if (loading && faqs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">FAQ Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Add FAQ
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="Search FAQs"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filterCategory}
              label="Category"
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Page</InputLabel>
            <Select
              value={filterPage}
              label="Page"
              onChange={(e) => setFilterPage(e.target.value)}
            >
              <MenuItem value="">All Pages</MenuItem>
              {Object.entries(pageLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterActive === null ? '' : filterActive ? 'active' : 'inactive'}
              label="Status"
              onChange={(e) => {
                const val = e.target.value;
                setFilterActive(val === '' ? null : val === 'active');
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchFAQs}>
            Refresh
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={60}>Order</TableCell>
              <TableCell>Question</TableCell>
              <TableCell width={120}>Category</TableCell>
              <TableCell width={140}>Page</TableCell>
              <TableCell width={100}>Status</TableCell>
              <TableCell width={180}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFAQs.map((faq, index) => {
              const faqIndex = faqs.findIndex(f => f._id === faq._id);
              return (
                <TableRow key={faq._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveFAQ(faq._id, 'up')}
                        disabled={faqIndex === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveFAQ(faq._id, 'down')}
                        disabled={faqIndex === faqs.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getDisplayText(faq.question, 80)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getDisplayText(faq.answer, 60)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={categoryLabels[faq.category] || faq.category}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pageLabels[faq.page] || faq.page || 'Landing'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={faq.isActive ? 'Active' : 'Inactive'}
                      color={faq.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(faq)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={faq.isActive ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          color={faq.isActive ? 'default' : 'success'}
                          onClick={() => handleToggleStatus(faq)}
                        >
                          {faq.isActive ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(faq)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredFAQs.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <Typography variant="h6" color="text.secondary">
            No FAQs found
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate} sx={{ mt: 2 }}>
            Create First FAQ
          </Button>
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <FAQForm open={formDialogOpen} onClose={handleFormClose} faq={selectedFAQ} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete FAQ</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this FAQ? This action cannot be undone.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {getDisplayText(faqToDelete?.question)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FAQList;
